import { useEffect, useRef, type ReactNode } from 'react';

import { SegnoEliminato } from './SegnoEliminato.tsx';
import { StrisciaFermo } from './AvvisoFermo.tsx';

/**
 * Finestra di dialogo modale.
 *
 * Usa l'elemento `<dialog>` nativo invece di ricostruirlo con dei `div`:
 * il browser fornisce già il blocco del contenuto sottostante, la chiusura
 * con Esc e l'annuncio corretto ai lettori di schermo. Rifarlo a mano
 * significa quasi sempre dimenticarsi qualcosa.
 */
export function Dialogo({
  titolo,
  aperto,
  onChiudi,
  children,
}: {
  titolo: string;
  aperto: boolean;
  onChiudi: () => void;
  children: ReactNode;
}) {
  const riferimento = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialogo = riferimento.current;
    if (dialogo === null) return;

    if (aperto && !dialogo.open) dialogo.showModal();
    else if (!aperto && dialogo.open) dialogo.close();
  }, [aperto]);

  if (!aperto) return null;

  return (
    <dialog
      ref={riferimento}
      className="dialogo"
      aria-labelledby="titolo-dialogo"
      // Il browser emette `cancel` quando si preme Esc: senza intercettarlo,
      // il dialogo si chiuderebbe da solo lasciando lo stato di React
      // convinto che sia ancora aperto.
      //
      // 🔴 **Il controllo sul bersaglio non è pedanteria.** Anche
      // `<input type="file">` emette `cancel` — quando si chiude la finestra
      // di scelta del file senza sceglierne uno — e quell'evento **risale**
      // fino a qui. Senza il confronto, annullare la scelta di un allegato
      // chiudeva l'intero dialogo, buttando via tutto quello che c'era
      // scritto dentro il modulo.
      //
      // Segnalato da Enrico il 27/8/2026 sul dialogo di creazione, ma c'era da
      // molto prima anche nella scheda di dettaglio, dove `PannelloAllegati`
      // ha il suo campo file: due sintomi, una riga sola. Ogni campo file che
      // un domani finirà dentro un dialogo è già coperto da qui.
      onCancel={(e) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        onChiudi();
      }}
      onClose={onChiudi}
    >
      {/* Titolo e avviso stanno in un contenitore solo, ed è quello a restare
          ancorato in cima mentre il modulo scorre.

          ⚠️ Prima l'ancoraggio era sull'intestazione, e la striscia le stava
          sopra **fuori** dal blocco ancorato: in un modulo lungo abbastanza da
          doverlo scorrere — e `DialogoScadenza` lo è — il preavviso usciva dallo
          schermo al primo scorrimento, restando visibile solo tornando in cima.
          Cioè spariva proprio a chi sta compilando, che è l'unica persona per
          cui questa striscia esiste. */}
      <div className="dialogo-testa">
        <div className="dialogo-intestazione">
          <h2 id="titolo-dialogo">{titolo}</h2>
          <button type="button" className="bottone-piatto" onClick={onChiudi}>
            Chiudi
          </button>
        </div>
        {/* ⚠️ Ripetuta qui, e non è una svista.
            `showModal()` mette questo elemento nel *top layer* del browser,
            sopra qualunque `z-index`: la striscia che sta nel flusso della
            pagina, da qui dentro, è dietro il velo — sbiadita o invisibile.
            Questo è l'unico `<dialog>` dell'applicazione, quindi basta metterla
            qui una volta.

            Sta **dopo** il titolo, non prima: chi legge con un lettore di
            schermo incontra così il nome del modulo che sta per compilare prima
            dell'avviso, invece del contrario. */}
        <StrisciaFermo />
      </div>
      {children}
    </dialog>
  );
}

/**
 * Riepilogo della scadenza, ripetuto in testa a tutti i dialoghi.
 *
 * ⚠️ `soggettoEliminato` e `tipoEliminato` sono **obbligatori** e non
 * facoltativi, di proposito: un nome congelato si legge esattamente come un
 * nome vivo, e un valore predefinito a `false` significherebbe che il punto di
 * chiamata che se ne dimentica mostra una scadenza orfana come se fosse
 * normale. Meglio un errore di compilazione che una riga che mente.
 */
export function RiepilogoScadenza({
  soggetto, soggettoEliminato, codice, reparto, tipo, tipoEliminato, dataPrevista, giorni,
}: {
  soggetto: string;
  soggettoEliminato: boolean;
  codice: string | null;
  reparto: string | null;
  tipo: string;
  tipoEliminato: boolean;
  dataPrevista: string;
  giorni: number;
}) {
  return (
    <dl className="riepilogo-scadenza">
      <dt>Soggetto</dt>
      <dd>
        <strong>{soggetto}</strong>
        {soggettoEliminato && <SegnoEliminato cosa="soggetto" />}
        {codice !== null && <span className="tenue"> · {codice}</span>}
        {reparto !== null && <span className="tenue"> · {reparto}</span>}
      </dd>

      <dt>Tipo</dt>
      <dd>
        <strong>{tipo}</strong>
        {tipoEliminato && <SegnoEliminato cosa="tipo" />}
      </dd>

      <dt>Data prevista</dt>
      <dd>
        <strong>{dataPrevista}</strong>
        <span className="tenue">
          {giorni < 0
            ? ` (${Math.abs(giorni)} giorni di ritardo)`
            : giorni === 0
              ? ' (scade oggi)'
              : ` (fra ${giorni} giorni)`}
        </span>
      </dd>
    </dl>
  );
}
